import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_CLICK_ADDRESS_PAGE_SHARE_ICON, GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON } from 'constants/events';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import type { Breadcrumb } from 'components/breadcrumbs';
import type { HeaderSectionData } from './types';

export interface UseHeaderSectionParams {
  /** Breadcrumb items */
  breadcrumbs: readonly Breadcrumb[];
  /** Whether the listing is saved/liked */
  isSaved: boolean;
  /** Share URL */
  shareUrl: string;
  /** Share text */
  shareText: string;
  /** Callback when like is toggled */
  onLikeToggle: () => void;
}

/**
 * useHeaderSection
 * 
 * Hook that prepares data for ListingHeaderSection.
 * Handles i18n, analytics, and share logic.
 * 
 * Pattern: Raw Data → Formatted Section Data
 * 
 * @param params - Raw listing/page data
 * @returns Formatted data ready for ListingHeaderSection
 * 
 * @example
 * ```tsx
 * const headerData = useHeaderSection({
 *   breadcrumbs,
 *   isSaved,
 *   shareUrl,
 *   shareText,
 *   onLikeToggle: () => onSaveListingClick?.(listing.id),
 * });
 * return <ListingHeaderSection sectionId="header" data={headerData} />;
 * ```
 */
export function useHeaderSection({
  breadcrumbs,
  isSaved,
  shareUrl,
  shareText,
  onLikeToggle,
}: UseHeaderSectionParams): HeaderSectionData {
  const { t } = useTranslation();
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  const handleShareClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_SHARE_ICON);
    
    if (typeof navigator.share !== 'undefined') {
      navigator.share({ 
        title: document.title, 
        url: shareUrl, 
        text: shareText,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShowCopiedTooltip(true);
        setTimeout(() => setShowCopiedTooltip(false), 3000);
      }).catch(() => {});
    }
  }, [shareUrl, shareText]);

  const handleLikeClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON);
    onLikeToggle();
  }, [onLikeToggle]);

  return useMemo(() => ({
    breadcrumbs,
    actions: {
      isLiked: isSaved,
      onLikeClick: handleLikeClick,
      onShareClick: handleShareClick,
      likeLabel: t(LOCALE_COMMON_PATHS.LISTING_HEADER_ACTIONS_LIKE),
      shareLabel: t(LOCALE_COMMON_PATHS.LISTING_HEADER_ACTIONS_SHARE_LISTING),
      copiedTooltip: t(LOCALE_COMMON_PATHS.LISTING_HEADER_ACTIONS_LINK_COPIED),
      showCopiedTooltip,
    },
  }), [breadcrumbs, isSaved, handleLikeClick, handleShareClick, t, showCopiedTooltip]);
}
